import { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config';
import { getAccessToken, logout } from '../../utils/auth';

function ChapterTitleEditor({ projectId, title, onTitleSaved, editable = true, children }) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [titleUpdateError, setTitleUpdateError] = useState('');

  const handleTitleClick = () => {
    if (!editable) return;
    if (title) {
      setEditedTitle(title);
      setIsEditingTitle(true);
      setTitleUpdateError('');
    }
  };

  const handleTitleSave = async () => {
    if (!editedTitle.trim()) {
      setTitleUpdateError('Title cannot be empty');
      return;
    }

    try {
      const token = getAccessToken();

      if (!token) {
        setTitleUpdateError('No access token found. Please login first.');
        logout();
        return;
      }

      await axios.patch(
        getApiUrl(`projects/${projectId}`),
        { title: editedTitle },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      onTitleSaved(editedTitle);
      setIsEditingTitle(false);
      setTitleUpdateError('');
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401) {
          setTitleUpdateError('Unauthorized: Please login again.');
        } else {
          setTitleUpdateError(
            `Error: ${err.response.data.message || err.response.statusText}`,
          );
        }
      } else if (err.request) {
        setTitleUpdateError('Network error: Could not connect to server');
      } else {
        setTitleUpdateError('An unexpected error occurred');
      }
    }
  };

  const handleTitleCancel = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
    setTitleUpdateError('');
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      handleTitleCancel();
    }
  };

  return (
    <>
      {isEditingTitle ? (
        <div className="flex items-center gap-2 mb-2 w-full">
          <input
            type="text"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 flex-1 min-w-0"
            autoFocus
          />
          <button
            onClick={handleTitleSave}
            className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex-shrink-0"
            title="Save"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </button>
          <button
            onClick={handleTitleCancel}
            className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex-shrink-0"
            title="Cancel"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <h1
            className={`text-3xl font-bold text-gray-900 dark:text-white inline-flex items-center gap-2 ${
              editable ? 'cursor-pointer hover:text-primary transition-colors' : ''
            }`}
            onClick={handleTitleClick}
            title={editable ? 'Click to edit title' : undefined}
          >
            {title || 'File Chapters'}
            {editable && title && (
              <svg
                className="w-5 h-5 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            )}
          </h1>
          {children}
        </div>
      )}
      {titleUpdateError && (
        <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <p className="text-sm text-red-600 dark:text-red-400">
            {titleUpdateError}
          </p>
        </div>
      )}
    </>
  );
}

export default ChapterTitleEditor;
